import React, { useState, useEffect } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { LoyaltyLogModels, loyaltyLogApi } from '@getgreenline/loyalties'
import { Alert, Timeline } from 'antd-v4'
import { THEME_COLORS } from '@getgreenline/shared'
import { formatDateLongMonth } from '../../utilities/date'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props {
  customerId?: string
}

const _CustomerLoyaltyLogs = ({ currentCompanyStore, customerId }: InjectedProps & Props) => {
  const [loyaltyLogs, setLoyaltyLogs] = useState<LoyaltyLogModels.ILoyaltyLogContract[]>([])

  useEffect(() => {
    getLoyaltyLogs()
  }, [])

  const getLoyaltyLogs = async () => {
    const { company } = currentCompanyStore || {}
    if (company && customerId) {
      const loyaltyLogs = await loyaltyLogApi.getLoyaltyLogs(company.id, { customerId })
      if (loyaltyLogs) {
        setLoyaltyLogs(loyaltyLogs)
      }
    }
  }

  const renderColouredAmount = (amount: number): JSX.Element => {
    const style: React.CSSProperties = {
      fontWeight: 'bold',
      color: amount > 0 ? THEME_COLORS.BRAND.retailAlt : THEME_COLORS.ACTIONS.danger,
    }
    return <div style={style}>Amount: {`${amount.toLocaleString('en-CA')}`}</div>
  }

  const getEmployeeName = (loyaltyLogIndex: number) => {
    const { users } = currentCompanyStore || {}
    if (!users) return ''

    const employee = users.find((user) => user.id === loyaltyLogs[loyaltyLogIndex].employeeId)
    return employee ? `Employee: ${employee.name}` : ''
  }

  return loyaltyLogs.length > 0 ? (
    <Timeline style={{ maxHeight: '60vh', overflow: 'scroll', paddingTop: 10 }}>
      {loyaltyLogs.length >= 500 && (
        <Alert
          message=''
          className='mb-3'
          description={<span>You are seeing the 500 most recent records</span>}
          type='info'
          showIcon
        />
      )}
      {loyaltyLogs.map((loyaltyLog, index) => {
        return (
          <Timeline.Item key={loyaltyLog.id} color={THEME_COLORS.BRAND.retailAlt}>
            <div className='font-weight-bold'>{`${formatDateLongMonth(
              loyaltyLog.createDate,
            )}`}</div>
            {!loyaltyLog.paymentId && !loyaltyLog.metadata && (
              <div className='font-weight-bold'>Manually updated</div>
            )}
            {!loyaltyLog.paymentId &&
              loyaltyLog.metadata &&
              (() => {
                const metadata = JSON.parse(loyaltyLog.metadata)

                return (
                  <>
                    {metadata.externalSourceId && (
                      <div>
                        <span className='font-weight-bold'>External Source ID:</span>{' '}
                        {metadata.externalSourceId}
                      </div>
                    )}
                    {metadata.externalSourceName && (
                      <div>
                        <span className='font-weight-bold'>External Source Name:</span>{' '}
                        {metadata.externalSourceName}
                      </div>
                    )}
                  </>
                )
              })()}
            <div>Balance: {`${loyaltyLog.newAmount?.toLocaleString('en-CA') ?? 0}`}</div>
            {renderColouredAmount(loyaltyLog.amount)}
            <div>Previous amount: {`${loyaltyLog.oldAmount?.toLocaleString('en-CA') ?? 0}`}</div>
            <div>{getEmployeeName(index)}</div>
          </Timeline.Item>
        )
      })}
    </Timeline>
  ) : null
}

export const CustomerLoyaltyLogs = inject('currentCompanyStore')(observer(_CustomerLoyaltyLogs))
